import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { X, Plus } from "lucide-react";
import { FrameworkCombobox, FrameworksProvider } from "@/components/ui/FrameworkCombobox";
import { RisksCombobox, RisksProvider } from "@/components/ui/RisksCombobox";
import { ObjectivesCombobox, ObjectivesProvider } from "@/components/ui/ObjectivesCombobox";
import { LegalObligationsCombobox, LegalObligationsProvider } from "@/components/ui/LegalObligationsCombobox";

interface Risk {
  id?: string | number;
  riskID?: number;
  riskIDstatus?: string;
  risk: string;
  dateAdded?: string | { seconds: number; nanoseconds: number };
}

interface Objective {
  id?: string | number;
  objectiveText: string;
  objectiveTargetDate?: string | { seconds: number; nanoseconds: number } | Date;
  dateAdded?: string | { seconds: number; nanoseconds: number };
}

interface LegalObligation {
  id?: string | number;
  requirement: string;
  updateMethod?: string;
  dateAdded?: string | { seconds: number; nanoseconds: number };
}

interface Framework {
  id?: string;
  standard: string;
  controlID?: string;
  controlName?: string;
  controlDescription?: string;
  framework?: string;
  controlApplicable?: boolean;
}

interface IssueDetails {
  issueType: string;
  risks: Risk[];
  objectives: Objective[];
  legalObligations: LegalObligation[];
  frameworks: Framework[];
}

interface Issue {
  id?: string;
  type: string;
  name: string;
  description: string;
  details: IssueDetails;
}

const issueTypes = [
  { value: 'internal', label: 'Internal' },
  { value: 'external', label: 'External' },
];

const issueDetailTypes = [
  { value: 'political', label: 'Political' },
  { value: 'economic', label: 'Economic' },
  { value: 'social', label: 'Social' },
  { value: 'technological', label: 'Technological' },
  { value: 'legal', label: 'Legal' },
  { value: 'environmental', label: 'Environmental' },
];

interface InternalExternalIssuesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  issue: Issue | null;
  onSave: (issue: Issue) => void;
  isReadOnly?: boolean;
}

export default function InternalExternalIssuesDialog({
  open,
  onOpenChange,
  issue,
  onSave,
  isReadOnly = false
}: InternalExternalIssuesDialogProps) {
  const [currentIssue, setCurrentIssue] = useState<Issue>({
    type: 'internal',
    name: '',
    description: '',
    details: {
      issueType: '',
      risks: [],
      objectives: [],
      legalObligations: [],
      frameworks: []
    }
  });

  // Removed individual state for new items as they're now handled by comboboxes

  // Update local state when issue prop changes
  useEffect(() => {
    if (issue) {
      // Transform data to match our expected structure
      const transformDate = (date: any) => {
        if (!date) return '';
        if (date.seconds) {
          return new Date(date.seconds * 1000).toISOString().split('T')[0];
        }
        return date;
      };

      console.log('[InternalExternalIssuesDialog] Issue:', issue);
      console.log('[InternalExternalIssuesDialog] Details:', issue.details);
      console.log('[InternalExternalIssuesDialog] Frameworks:', issue.details.frameworks);

      const frameworks = (issue.details?.frameworks || []).map(f => {
        // If it's already an object with an id, use it as is
        if (typeof f === 'object' && f !== null) {
          return {
            ...f,
            id:  f.framework || f.controlID || f.standard,
            standard: f.standard || f.framework || 'Unknown',
            name: f.name || f.standard || f.framework || 'Unknown' // Add name for display
          };
        }
        // If it's a string, use it as both id and standard
        if (typeof f === 'string') {
          return {
            id: f,
            standard: f,
            name: f
          };
        }
        // Fallback for any other case
        return {
          id: `fw-${Date.now()}`,
          standard: 'Unknown',
          name: 'Unknown'
        };
      });
      const risks = (issue.details?.risks || []).map(r => ({
        ...r,
        id: String(r.riskID || r.id || `risk-${Date.now()}`),
        risk: r.risk || 'Unnamed Risk',
        dateAdded: transformDate(r.dateAdded)
      }));

      const objectives = (issue.details?.objectives || []).map(o => ({
        ...o,
        id: String(o.id || `obj-${Date.now()}`),
        objectiveText: o.objectiveText || 'Unnamed Objective',
        dateAdded: transformDate(o.dateAdded)
      }));

      const legalObligations = (issue.details?.legalObligations || []).map(l => ({
        ...l,
        id: String(l.id || `legal-${Date.now()}`),
        requirement: l.requirement || 'Unnamed Obligation',
        dateAdded: transformDate(l.dateAdded)
      }));

      setCurrentIssue({
        ...issue,
        type: issue.type.toLowerCase() || 'internal',
        name: issue.name || '',
        description: issue.description || '',
        details: {
          ...issue.details,
          issueType: issue.details?.issueType || '',
          risks,
          objectives,
          legalObligations,
          frameworks
        }
      });
    } else {
      setCurrentIssue({
        id: undefined, // Ensure no ID for new issues
        type: 'internal',
        name: '',
        description: '',
        details: {
          issueType: '',
          risks: [],
          objectives: [],
          legalObligations: [],
          frameworks: []
        }
      });
    }

    // Reset is handled by the combobox components
  }, [issue, open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(currentIssue);
  };

  const handleRisksChange = (selectedRiskIds: string[]) => {
    setCurrentIssue(prev => ({
      ...prev,
      details: {
        ...prev.details,
        risks: selectedRiskIds.map(id => ({
          id,
          risk: `Risk ${id}`, // This will be replaced by actual risk data from the combobox
          dateAdded: new Date().toISOString()
        }))
      }
    }));
  };

  const handleObjectivesChange = (selectedObjectiveIds: string[]) => {
    setCurrentIssue(prev => ({
      ...prev,
      details: {
        ...prev.details,
        objectives: selectedObjectiveIds.map(id => ({
          id,
          objectiveText: `Objective ${id}`, // This will be replaced by actual objective data from the combobox
          dateAdded: new Date().toISOString()
        }))
      }
    }));
  };

  const handleLegalObligationsChange = (selectedLegalIds: string[]) => {
    setCurrentIssue(prev => ({
      ...prev,
      details: {
        ...prev.details,
        legalObligations: selectedLegalIds.map(id => ({
          id,
          requirement: `Legal Obligation ${id}`, // This will be replaced by actual legal obligation data from the combobox
          dateAdded: new Date().toISOString()
        }))
      }
    }));
  };

  const handleFrameworkSelect = (frameworkIds: string[]) => {
    // Get current framework IDs for comparison
    const currentFrameworkIds = currentIssue.details.frameworks.map(f => f.id).filter(Boolean) as string[];

    // Find newly added frameworks
    const addedFrameworks = frameworkIds
      .filter(id => !currentFrameworkIds.includes(id))
      .map(id => ({
        id,
        standard: id,
        framework: id
      }));

    // Find removed frameworks
    const removedFrameworkIds = currentFrameworkIds.filter(id => !frameworkIds.includes(id));

    // Update frameworks by removing the removed ones and adding the new ones
    const updatedFrameworks = [
      ...currentIssue.details.frameworks.filter(f => !removedFrameworkIds.includes(f.id as string)),
      ...addedFrameworks
    ];

    setCurrentIssue(prev => ({
      ...prev,
      details: {
        ...prev.details,
        frameworks: updatedFrameworks
      }
    }));
  };

  const removeFramework = (id: string) => {
    setCurrentIssue(prev => ({
      ...prev,
      details: {
        ...prev.details,
        frameworks: prev.details.frameworks.filter(f => f.id !== id)
      }
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] overflow-y-auto bg-secondary">
        <DialogHeader>
          <DialogTitle>{isReadOnly ? "View" : "Edit"} Issue{issue?.id ? 'Edit Issue' : 'Add New Issue'}</DialogTitle>
          <DialogDescription>
            {issue?.id ? 'Update the issue details below.' : 'Fill in the details for the new issue.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Type</Label>
            <Select
              value={currentIssue.type}
              onValueChange={(value) => !isReadOnly && setCurrentIssue(prev => ({ ...prev, type: value }))}
              disabled={isReadOnly}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {issueTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Name</Label>
            <Input
              id="issueName"
              value={currentIssue.name}
              onChange={(e) => !isReadOnly && setCurrentIssue(prev => ({ ...prev, name: e.target.value }))}
              className="w-full"
              readOnly={isReadOnly}
            />
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              id="issueDescription"
              value={currentIssue.description}
              onChange={(e) => !isReadOnly && setCurrentIssue(prev => ({ ...prev, description: e.target.value }))}
              className="w-full"
              rows={3}
              readOnly={isReadOnly}
            />
          </div>

          <div className="space-y-2">
            <Label>Issue Type</Label>
            <Select
              value={currentIssue.details.issueType}
              onValueChange={(value) => !isReadOnly && setCurrentIssue(prev => ({
                ...prev,
                details: { ...prev.details, issueType: value }
              }))}
              disabled={isReadOnly}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select issue type" />
              </SelectTrigger>
              <SelectContent>
                {issueDetailTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Risks</Label>
            <RisksProvider>
              {isReadOnly ? (
                <div className="text-sm text-muted-foreground py-2">
                  {currentIssue.details.risks.length > 0
                    ? currentIssue.details.risks.map(r =>
                      <div key={String(r.id || r.riskID)} className="mb-1">
                        {String(r.id || r.riskID).startsWith('RSK') ? r.id || r.riskID : `RSK-${r.id || r.riskID}`}: {r.risk}
                      </div>
                    )
                    : 'No risks assigned'}
                </div>
              ) : (
                <RisksCombobox
                  selected={currentIssue.details.risks.map(r => String(r.id || r.riskID))}
                  setSelected={handleRisksChange}
                  placeholder="Select risks..."
                  className="w-full"
                />
              )}
            </RisksProvider>
            {/* {!isReadOnly && (
              <div className="flex flex-wrap gap-2 mt-2">
                {currentIssue.details.risks.map((risk) => (
                  <Badge key={String(risk.id || risk.riskID)} variant="outline" className="flex items-center gap-1">
                    {String(risk.id || risk.riskID).startsWith('RSK') ? risk.id || risk.riskID : `RSK-${risk.id || risk.riskID}`}
                    <button
                      type="button"
                      onClick={() => handleRisksChange(
                        currentIssue.details.risks
                          .filter(r => String(r.id || r.riskID) !== String(risk.id || risk.riskID))
                          .map(r => String(r.id || r.riskID))
                      )}
                      className="ml-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )} */}
          </div>

          <div className="space-y-2">
            <Label>Objectives</Label>
            <ObjectivesProvider>
              {isReadOnly ? (
                <div className="text-sm text-muted-foreground py-2">
                  {currentIssue.details.objectives.length > 0
                    ? currentIssue.details.objectives.map(o =>
                      <div key={String(o.id)} className="mb-1">
                        {String(o.id).startsWith('OBJ') ? o.id : `OBJ-${o.id}`}: {o.objectiveText}
                      </div>
                    )
                    : 'No objectives assigned'}
                </div>
              ) : (
                <ObjectivesCombobox
                  selected={currentIssue.details.objectives.map(o => String(o.id))}
                  setSelected={handleObjectivesChange}
                  placeholder="Select objectives..."
                  className="w-full"
                />
              )}
            </ObjectivesProvider>
            {/* {!isReadOnly && (
              <div className="flex flex-wrap gap-2 mt-2">
                {currentIssue.details.objectives.map((obj) => (
                  <Badge key={String(obj.id)} variant="outline" className="flex items-center gap-1">
                    {String(obj.id).startsWith('OBJ') ? obj.id : `OBJ-${obj.id}`}
                    <button
                      type="button"
                      onClick={() => handleObjectivesChange(
                        currentIssue.details.objectives
                          .filter(o => String(o.id) !== String(obj.id))
                          .map(o => String(o.id))
                      )}
                      className="ml-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )} */}
          </div>

          <div className="space-y-2">
            <Label>Legal Obligations</Label>
            <LegalObligationsProvider>
              {isReadOnly ? (
                <div className="text-sm text-muted-foreground py-2">
                  {currentIssue.details.legalObligations.length > 0
                    ? currentIssue.details.legalObligations.map(l =>
                      <div key={String(l.id)} className="mb-1">
                        {String(l.id).startsWith('LEO') ? l.id : `LEO-${l.id}`}: {l.requirement}
                      </div>
                    )
                    : 'No legal obligations assigned'}
                </div>
              ) : (
                <LegalObligationsCombobox
                  selected={currentIssue.details.legalObligations.map(l => String(l.id))}
                  setSelected={handleLegalObligationsChange}
                  placeholder="Select legal obligations..."
                  className="w-full"
                />
              )}
            </LegalObligationsProvider>
            {/* {!isReadOnly && (
              <div className="flex flex-wrap gap-2 mt-2">
                {currentIssue.details.legalObligations.map((legal) => (
                  <Badge key={String(legal.id)} variant="secondary" className="flex items-center gap-1">
                    {String(legal.id).startsWith('LEO') ? legal.id : `LEO-${legal.id}`}
                    <button
                      type="button"
                      onClick={() => handleLegalObligationsChange(
                        currentIssue.details.legalObligations
                          .filter(l => String(l.id) !== String(legal.id))
                          .map(l => String(l.id))
                      )}
                      className="ml-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )} */}
          </div>

          <div className="space-y-2">
            <Label>Frameworks</Label>
            <FrameworksProvider>
              {isReadOnly ? (
                <div className="text-sm text-muted-foreground py-2">
                  {currentIssue.details.frameworks.length > 0
                    ? currentIssue.details.frameworks.map((f, idx) =>
                      <div key={f.id || idx} className="mb-1">
                        {f.standard || f.framework || 'Unknown Framework'}
                      </div>
                    )
                    : 'No frameworks assigned'}
                </div>
              ) : (
                <FrameworkCombobox
                  selected={currentIssue.details.frameworks.map(f => f.id).filter(Boolean) as string[]}
                  setSelected={handleFrameworkSelect}
                  placeholder="Select frameworks..."
                  className="w-full"
                />
              )}
            </FrameworksProvider>
            {/* <div className="flex flex-wrap gap-2 mt-2">
              {currentIssue.details.frameworks.map((framework) => (
                <Badge 
                  key={framework.id} 
                  variant="outline" 
                  className="flex items-center gap-1"
                >
                  {framework.standard}
                  <button
                    type="button"
                    onClick={() => framework.id && removeFramework(framework.id)}
                    className="ml-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div> */}
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {isReadOnly ? "Close" : "Cancel"}
            </Button>
            {!isReadOnly && <Button type="submit">Save</Button>}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}